import { LtrText } from "@/components/LtrText";
import React, { useMemo, useState } from "react";
import { Search, Bot, RefreshCw, MessageCircle, Check, CheckCheck, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { alertsEnabled, setAlertsEnabled, requestNotifyPermission, beep } from "./WaAlerts";
import { Switch } from "@/components/ui/switch";
import { listTime, initials, avatarHue } from "./chatUtils";

export const Avatar = ({ c, size = "w-12 h-12 text-base" }) => (
  <div className={`${size} rounded-full grid place-items-center font-extrabold text-white shrink-0 select-none`} style={{ background: `hsl(${avatarHue(c?.wa_id)} 45% 45%)` }} aria-hidden>
    {initials(c)}
  </div>
);

const Row = ({ c, active, onClick }) => {
  const mine = !!c.last_out_at && (!c.last_inbound_at || c.last_out_at > c.last_inbound_at);
  return (
    <button onClick={onClick} className={`w-full text-start flex items-center gap-3 px-3 py-2.5 transition-colors ${active ? "bg-primary/10" : "hover:bg-secondary/70 active:bg-secondary"}`} data-testid={`wa-contact-${c.wa_id}`}>
      <Avatar c={c} />
      <div className="flex-1 min-w-0 border-b border-border/60 pb-2.5 -mb-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="font-bold text-[15px] truncate"><bdi dir="ltr">{c.name || `+${c.wa_id}`}</bdi></span>
          <span className={`text-[11px] shrink-0 ${c.unread > 0 ? "text-emerald-500 font-bold" : "text-muted-foreground"}`} dir="ltr">{listTime(c.last_inbound_at || c.last_out_at)}</span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-[13px] text-muted-foreground truncate flex items-center gap-1 min-w-0">
            {mine && <CheckCheck className="w-3.5 h-3.5 shrink-0 text-sky-500" />}
            <span className="truncate" dir="auto"><LtrText text={c.last_text || "…"} plain /></span>
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            {c.needs_human && <span className="rounded-full bg-amber-500 text-white px-1.5 py-px text-[10px] font-bold" data-testid={`wa-needs-human-${c.wa_id}`}>موظف</span>}
            {c.bot_enabled === false && <Bot className="w-3.5 h-3.5 text-muted-foreground/60" title="البوت متوقف لهذا العميل" />}
            {c.unread > 0 && <span className="min-w-[20px] h-5 rounded-full bg-emerald-500 text-white grid place-items-center px-1.5 text-[11px] font-bold" data-testid={`wa-unread-${c.wa_id}`}>{c.unread}</span>}
          </span>
        </div>
      </div>
    </button>
  );
};

const FILTERS = [["all", "الكل"], ["unread", "غير مقروء"], ["human", "يحتاج موظف"], ["bot_off", "البوت متوقف"]];

const AlertsBell = () => {
  const [on, setOn] = useState(alertsEnabled());
  const toggle = async () => {
    const next = !on;
    setAlertsEnabled(next); setOn(next);
    if (next) {
      beep();
      const p = await requestNotifyPermission();
      toast.success(p === "granted" ? "التنبيهات مفعّلة: صوت + إشعار متصفح عند أي رسالة أو تصعيد جديد" : "التنبيهات مفعّلة بالصوت داخل المنصة" + (p === "denied" ? " — إشعارات المتصفح محظورة من إعدادات الموقع" : ""));
    } else toast("أوقفت تنبيهات واتساب");
  };
  return (
    <button onClick={toggle} className={`w-9 h-9 rounded-full grid place-items-center transition-colors ${on ? "text-emerald-500 hover:bg-emerald-500/10" : "text-muted-foreground hover:bg-secondary"}`} title={on ? "التنبيهات مفعّلة (صوت + إشعار)" : "تفعيل التنبيهات"} aria-label="التنبيهات" data-testid="wa-alerts-toggle" data-state={on ? "on" : "off"}>
      {on ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
    </button>
  );
};

export const ChatList = ({ contacts, sel, onSelect, title, subtitle, emptyText, onRefresh, globalBot, onGlobalBot }) => {
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const rows = useMemo(() => contacts.filter((c) => {
    if (f === "unread" && !(c.unread > 0)) return false;
    if (f === "human" && !c.needs_human) return false;
    if (f === "bot_off" && c.bot_enabled !== false) return false;
    const s = q.trim().toLowerCase();
    return !s || (c.name || "").toLowerCase().includes(s) || (c.wa_id || "").includes(s) || (c.last_text || "").toLowerCase().includes(s);
  }), [contacts, q, f]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-card" data-testid="wa-contacts">
      <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-xl font-extrabold font-heading flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-500" /> {title}</h3>
          <div className="text-[11px] text-muted-foreground truncate" dir="auto">{subtitle}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onGlobalBot && <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Bot className="w-3.5 h-3.5" /><Switch checked={!!globalBot} onCheckedChange={onGlobalBot} data-testid="wa-bot-global-toggle" /></label>}
          <AlertsBell />
          <button onClick={onRefresh} className="w-9 h-9 rounded-full grid place-items-center hover:bg-secondary transition-colors" data-testid="wa-inbox-refresh" aria-label="تحديث"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="px-3 pb-2">
        <label className="flex items-center gap-2 rounded-full bg-secondary/80 px-3 h-10">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث باسم أو رقم أو نص…" className="bg-transparent outline-none text-sm flex-1 min-w-0" data-testid="wa-search-input" />
        </label>
      </div>
      <div className="px-3 pb-2 flex gap-1.5 overflow-x-auto no-scrollbar">
        {FILTERS.map(([k, l]) => <button key={k} onClick={() => setF(k)} className={`rounded-full px-3 h-7 text-xs font-bold whitespace-nowrap border transition-colors ${f === k ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-500" : "border-border text-muted-foreground hover:bg-secondary"}`} data-testid={`wa-filter-${k}`}>{l}</button>)}
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain">
        {rows.length === 0 && <div className="text-xs text-muted-foreground p-6 text-center" data-testid="wa-inbox-empty">{contacts.length === 0 ? emptyText : "لا نتائج مطابقة"}</div>}
        {rows.map((c) => <Row key={c.wa_id} c={c} active={c.wa_id === sel} onClick={() => onSelect(c.wa_id)} />)}
      </div>
      <div className="px-4 py-2 text-[10px] text-muted-foreground border-t border-border flex items-center gap-1"><Check className="w-3 h-3" /> يُرسل المشرف من رقم النشاط نفسه — العميل يرى ردّك كرسالة واتساب عادية</div>
    </div>
  );
};
